import { t } from "i18next";
import { match, P } from "ts-pattern";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  longTextSchema,
  nonFutureDateTimeSchema,
  shortTextSchema,
  yesNoSchema,
  yesOnlySchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "didNotArrive",
    type: "switch",
    ...new FieldBuilder()
      .withNullableSchema(yesOnlySchema)
      .captureValueLabel()
      .build(),
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("arrivePlaceOfSafety.didNotArrive"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
  })
  .withFieldConditionallyVisible({
    fieldName: "arrivalDateTime",
    type: "date-time",
    ...new FieldBuilder().withNullableSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("arrivePlaceOfSafety.arrivalDate"),
    timeLabel: tk("arrivePlaceOfSafety.arrivalTime"),
    disableFuture: true,
    isVisible: ({ formValues }) =>
      match(formValues.didNotArrive)
        .with({ valueLabel: P._, value: "yes" }, () => false)
        .with(null, undefined, () => true)
        .exhaustive(),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "advisePhysicallyEnter",
    type: "banner",
    bannerType: "warning",
    isVisible: ({ formValues }) =>
      match(formValues.didNotArrive)
        .with({ valueLabel: P._, value: "yes" }, () => false)
        .with(null, undefined, () => true)
        .exhaustive(),
    body: tk("arrivePlaceOfSafety.advisePhysicallyEnter"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "adviseCannotShare",
    type: "banner",
    bannerType: "info",
    isVisible: ({ formValues }) =>
      match(formValues.didNotArrive)
        .with({ valueLabel: P._, value: "yes" }, () => true)
        .with(null, undefined, () => false)
        .exhaustive(),
    body: ({ incidentWorkItem }) => ({
      translationKey: tk("arrivePlaceOfSafety.adviseCannotShare"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
  })
  .withFieldConditionallyVisible({
    fieldName: "wasEntryDelayed",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
    isVisible: ({ formValues }) =>
      match(formValues.didNotArrive)
        .with({ valueLabel: P._, value: "yes" }, () => false)
        .with(null, undefined, () => true)
        .exhaustive(),
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("arrivePlaceOfSafety.wasEntryDelayed"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyWasEntryDelayed",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "posOrEdClosed" as const,
          "nhsAdvisedWaitOutside" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "arrivePlaceOfSafety.whyWasEntryDelayed.options.posOrEdClosed",
        ),
        value: "posOrEdClosed",
      },
      {
        label: tk(
          "arrivePlaceOfSafety.whyWasEntryDelayed.options.nhsAdvisedWaitOutside",
        ),
        value: "nhsAdvisedWaitOutside",
      },
      {
        label: tk("arrivePlaceOfSafety.whyWasEntryDelayed.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) => formValues.wasEntryDelayed?.value === "yes",
    label: tk("arrivePlaceOfSafety.whyWasEntryDelayed.title"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyWasEntryDelayedOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.whyWasEntryDelayed?.value === "other",
    label: tk("arrivePlaceOfSafety.whyWasEntryDelayedOther.title"),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "howLongWasEntryDelayed",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        // Every value ever offered stays here: an incident recorded against one
        // must remain parseable and re-saveable for the life of the record.
        // `60+` is deliberately absent from the options below, because an
        // open-ended "over an hour" overlaps `120+` and leaves a long wait
        // matching two answers.
        z.enum([
          "0-15" as const,
          "15-30" as const,
          "30-60" as const,
          "60+" as const,
          "60-120" as const,
          "120+" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk("arrivePlaceOfSafety.howLongWasEntryDelayed.options.0-15"),
        value: "0-15",
      },
      {
        label: tk("arrivePlaceOfSafety.howLongWasEntryDelayed.options.15-30"),
        value: "15-30",
      },
      {
        label: tk("arrivePlaceOfSafety.howLongWasEntryDelayed.options.30-60"),
        value: "30-60",
      },
      {
        label: tk("arrivePlaceOfSafety.howLongWasEntryDelayed.options.60-120"),
        value: "60-120",
      },
      {
        label: tk("arrivePlaceOfSafety.howLongWasEntryDelayed.options.120+"),
        value: "120+",
      },
    ],
    isVisible: ({ formValues }) => formValues.wasEntryDelayed?.value === "yes",
    label: tk("arrivePlaceOfSafety.howLongWasEntryDelayed.title"),
  })
  .withFieldConditionallyVisible({
    fieldName: "methodOfTransport",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "alreadyAtPos" as const,
          "ambulance" as const,
          "policeVehicle" as const,
          "otherHealthVehicle" as const,
          "escorted" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: ({ incidentWorkItem }) =>
      [
        {
          label: tk(
            "arrivePlaceOfSafety.methodOfTransport.options.alreadyAtPos",
          ),
          value: "alreadyAtPos",
        },
        {
          label: tk("arrivePlaceOfSafety.methodOfTransport.options.ambulance"),
          value: "ambulance",
        },
        {
          label: tk(
            "arrivePlaceOfSafety.methodOfTransport.options.policeVehicle",
          ),
          value: "policeVehicle",
        },
        {
          label: tk(
            "arrivePlaceOfSafety.methodOfTransport.options.otherHealthVehicle",
          ),
          value: "otherHealthVehicle",
        },
        {
          label: tk("arrivePlaceOfSafety.methodOfTransport.options.escorted"),
          value: "escorted",
        },
        {
          label: tk("arrivePlaceOfSafety.methodOfTransport.options.other"),
          value: "other",
        },
      ].map((option) => ({
        label: {
          translationKey: option.label,
          translationOptions: {
            context: getWorkflowTranslationContext({ incidentWorkItem }),
          },
        },
        value: option.value,
      })),
    isVisible: ({ formValues }) =>
      match(formValues.didNotArrive)
        .with({ valueLabel: P._, value: "yes" }, () => false)
        .with(null, undefined, () => true)
        .exhaustive(),
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("arrivePlaceOfSafety.methodOfTransport.title"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
  })
  .withFieldConditionallyVisible({
    fieldName: "methodOfTransportOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.methodOfTransport?.value === "other",
    label: tk("arrivePlaceOfSafety.methodOfTransportOther.title"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyMethodOfTransportPoliceVehicle",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "ambulanceAvailableInTimeframe" as const,
          "ambulanceCrewRefused" as const,
          "riskAssessment" as const,
          "ambulanceNotRequested" as const,
          "ambulanceRetasked" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicle.options.ambulanceAvailableInTimeframe",
        ),
        value: "ambulanceAvailableInTimeframe",
      },
      {
        label: tk(
          "arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicle.options.ambulanceCrewRefused",
        ),
        value: "ambulanceCrewRefused",
      },
      {
        label: tk(
          "arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicle.options.riskAssessment",
        ),
        value: "riskAssessment",
      },
      {
        label: tk(
          "arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicle.options.ambulanceNotRequested",
        ),
        value: "ambulanceNotRequested",
      },
      {
        label: tk(
          "arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicle.options.ambulanceRetasked",
        ),
        value: "ambulanceRetasked",
      },
      {
        label: tk(
          "arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicle.options.other",
        ),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.methodOfTransport?.value === "policeVehicle",
    label: tk("arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicle.title"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyMethodOfTransportPoliceVehicleOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.methodOfTransport?.value === "policeVehicle" &&
      formValues.whyMethodOfTransportPoliceVehicle?.value === "other",
    label: tk(
      "arrivePlaceOfSafety.whyMethodOfTransportPoliceVehicleOther.title",
    ),
  })
  .withFormValidation(({ formValues }) =>
    formValues.arrivalDateTime === null && formValues.didNotArrive === null
      ? [
          {
            fieldName: "arrivalDateTime",
            message: "Required",
          },
          {
            fieldName: "didNotArrive",
            message: "Required",
          },
        ]
      : [],
  )
  .withPage([
    "didNotArrive",
    "arrivalDateTime",
    "advisePhysicallyEnter",
    "adviseCannotShare",
  ])
  .withPage([
    "wasEntryDelayed",
    "whyWasEntryDelayed",
    "whyWasEntryDelayedOther",
    "howLongWasEntryDelayed",
  ])
  .withPage([
    "methodOfTransport",
    "methodOfTransportOther",
    "whyMethodOfTransportPoliceVehicle",
    "whyMethodOfTransportPoliceVehicleOther",
  ])
  .build({ t });

export type ArrivePlaceOfSafetyFormData = ReturnType<
  typeof formSpecification
>["formType"];
